<template>
  <div class="tab-page-root bg-surface text-on-surface">
    <div ref="scrollContainer" class="tab-page-scroll">
    <header class="sticky top-0 w-full z-50 bg-surface dark:bg-surface-dim flex items-center justify-between px-container-margin py-base gap-gutter glass-effect"
      :class="{ 'shadow-md': scrolled, 'shadow-sm': !scrolled }"
    >
      <div class="flex-1 max-w-md">
        <div class="relative flex items-center bg-surface-container-high rounded-full px-stack-md py-2 border border-transparent focus-within:border-primary-container transition-all">
          <span class="material-symbols-outlined text-on-surface-variant mr-2" style="font-size: 20px;">search</span>
          <input
            ref="searchInput"
            v-model="searchQuery"
            class="bg-transparent border-none focus:ring-0 p-0 w-full font-body-md text-body-md placeholder-on-surface-variant"
            placeholder="搜索商品..."
            type="search"
            enterkeyhint="search"
            autocomplete="off"
            @input="onSearchInput"
            @compositionstart="searchComposing = true"
            @compositionend="onSearchCompositionEnd"
            @keyup.enter.prevent="submitSearch"
          />
          <button
            v-if="searchQuery"
            class="ml-1 p-1 text-on-surface-variant active:opacity-70"
            type="button"
            aria-label="清除搜索"
            @click="clearSearch"
          >
            <span class="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      </div>
    </header>

    <main class="pb-[80px]">
      <section class="px-container-margin pt-stack-md">
        <div
          class="relative h-[160px] rounded-xl overflow-hidden shadow-lg group active:scale-[0.99] transition-transform cursor-pointer"
          role="button"
          tabindex="0"
          @click="goToNearby"
          @keyup.enter="goToNearby"
        >
          <img
            class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 pointer-events-none"
            :src="heroImg"
            alt="校园二手交易"
          />
          <div class="absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-transparent flex flex-col justify-center px-stack-lg pointer-events-none">
            <h2 class="font-headline-sm text-headline-sm text-white mb-1">Panda Campus  闲置二手</h2>
            <p class="font-body-md text-body-md text-white/90">{{ circleHeroSubtitle }}</p>
            <div class="mt-stack-md">
              <span class="inline-flex items-center gap-1 bg-primary-container text-on-primary-container px-4 py-1.5 rounded-full font-label-md text-label-md">
                <span class="material-symbols-outlined text-[16px]">travel_explore</span>
                去圈子看看
              </span>
            </div>
          </div>
        </div>
      </section>

      <section class="pt-stack-lg">
        <div class="px-container-margin flex items-center justify-between mb-stack-sm">
          <h3 class="font-headline-sm text-headline-sm text-on-surface">留学攻略</h3>
          <button
            v-if="guideTotal > 4"
            class="font-label-md text-label-md text-primary active:opacity-70"
            type="button"
            @click="goToGuideList"
          >查看更多</button>
        </div>
        <div class="guide-scroll hide-scrollbar">
          <button
            v-for="guide in visibleGuides"
            :key="guide.id"
            class="guide-card bg-surface-container-lowest border border-outline-variant/30 shadow-sm active:scale-[0.98] transition-transform"
            type="button"
            @click="goToGuide(guide)"
          >
            <img
              v-if="guide.avatar"
              class="guide-card-avatar"
              :src="guide.avatar"
              :alt="guide.title"
            />
            <span v-else class="material-symbols-outlined guide-card-icon text-primary">school</span>
            <span class="font-body-md text-body-md text-on-surface guide-card-title">{{ guide.title }}</span>
            <span class="font-label-md text-[12px] leading-snug text-on-surface-variant guide-card-desc">{{ guide.tag || '' }}</span>
          </button>
        </div>
      </section>

      <section ref="productSection" class="px-container-margin pt-stack-lg">
        <div class="flex items-center justify-between mb-stack-md">
          <h3 class="font-headline-sm text-headline-sm text-on-surface">
            {{ activeSearchKeyword ? '搜索结果' : '最新发布' }}
          </h3>
          <span
            v-if="!activeSearchKeyword"
            class="font-label-md text-label-md text-primary cursor-pointer active:opacity-70"
            role="button"
            tabindex="0"
            @click="browseCategory('all')"
          >查看全部</span>
          <span
            v-else
            class="font-label-md text-label-md text-on-surface-variant"
          >「{{ activeSearchKeyword }}」</span>
        </div>

        <p v-if="productsLoading" class="font-body-md text-body-md text-on-surface-variant py-8 text-center">搜索中...</p>
        <p v-else-if="productsError" class="font-body-md text-body-md text-error py-8 text-center">{{ productsError }}</p>
        <div v-else-if="products.length === 0" class="py-10 text-center">
          <span class="material-symbols-outlined text-[40px] text-on-surface-variant">search_off</span>
          <p class="font-body-md text-body-md text-on-surface-variant mt-2">
            {{ activeSearchKeyword ? '没有找到相关商品' : '暂无商品' }}
          </p>
        </div>
        <div v-else class="masonry-grid">
          <a
            v-for="item in products"
            :key="item.id"
            class="bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm active:scale-[0.98] transition-all flex flex-col"
            href="javascript:void(0)"
            @click="goToProduct(item.id)"
          >
            <div class="aspect-square relative">
              <img class="w-full h-full object-cover" :src="item.image" :alt="item.alt" />
              <div class="card-fav-btn glass-effect">
                <span class="material-symbols-outlined text-on-surface-variant text-[18px]">favorite</span>
              </div>
            </div>
            <div class="p-stack-md flex flex-col flex-1">
              <h4 class="font-body-md text-body-md text-on-surface line-clamp-2 mb-1">{{ item.title }}</h4>
              <div class="mt-auto">
                <span class="font-price-display text-price-display text-primary-container block">{{ item.price }}</span>
                <div class="flex items-center justify-between mt-1 text-on-surface-variant opacity-70">
                  <div class="flex items-center gap-0.5 location-trigger" role="button" tabindex="0">
                    <span class="material-symbols-outlined" style="font-size: 10px;">location_on</span>
                    <span class="font-label-md text-label-md">{{ item.location }}</span>
                  </div>
                  <span class="font-label-md text-label-md">{{ item.time }}</span>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>
    </main>
    </div>
  </div>
</template>

<script>
import getApi from '@/api';
import store from '@/store';

const api = getApi();

const heroImg = require('@/assets/images/8dbe1ff1e569.jpg');
const FALLBACK_IMG = require('@/assets/images/07e08d5831a0.jpg');

export default {
  name: 'HomePage',
  data() {
    return {
      scrolled: false,
      searchQuery: '',
      heroImg,
      accountCircles: [],
      products: [],
      productsLoading: false,
      productsError: '',
      searchTimer: null,
      productsRequestId: 0,
      homeDataLoaded: false,
      guideListAll: [],
      guideTotal: 0,
      guideListLoading: false,
      activeSearchKeyword: '',
      searchComposing: false
    };
  },
  computed: {
    primaryCircleName() {
      const circle = this.accountCircles[0];
      if (!circle) return '';
      return circle.title || circle.momo || circle.city || '';
    },
    circleHeroSubtitle() {
      if (this.primaryCircleName) {
        return this.primaryCircleName + '同学在出售';
      }
      return '校园闲置，就近成交';
    },
    visibleGuides() {
      return this.guideListAll.slice(0, 4);
    }
  },
  methods: {
    browseCategory(catKey) {
      store.setActiveBrowseCategory(catKey);
      this.$router.push({ name: 'category-browse' });
    },
    goToNearby() {
      this.$router.push({ name: 'nearby' });
    },
    goToGuideList() {
      this.$router.push({ name: 'guide-list' });
    },
    goToGuide(guide) {
      if (guide && guide.id != null) {
        store.setActiveGuideId(String(guide.id));
        this.$router.push({ name: 'guide-detail' });
      }
    },
    goToProduct(id) {
      store.setActiveGoodsId(String(id));
      this.$router.push({ name: 'product-detail' });
    },
    getScrollContainer() {
      return this.$refs.scrollContainer || null;
    },
    onScroll() {
      const container = this.getScrollContainer();
      this.scrolled = container ? container.scrollTop > 10 : false;
    },
    getSearchKeyword() {
      return (this.searchQuery || '').trim();
    },
    onSearchInput() {
      // 中文输入法拼写过程中不请求，等 compositionend
      if (this.searchComposing) return;
      this.scheduleSearch();
    },
    onSearchCompositionEnd(event) {
      this.searchComposing = false;
      // 部分机型 compositionend 时 v-model 尚未同步，用事件值兜底
      if (event && event.target && typeof event.target.value === 'string') {
        this.searchQuery = event.target.value;
      }
      this.scheduleSearch();
    },
    scheduleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      // 输入停顿后自动加载，无需点搜索
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.fetchProducts({ scrollToResults: !!this.getSearchKeyword() });
      }, 300);
    },
    submitSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.fetchProducts({ scrollToResults: !!this.getSearchKeyword() });
    },
    clearSearch() {
      this.searchQuery = '';
      this.searchComposing = false;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.fetchProducts();
    },
    scrollToProductSection() {
      this.$nextTick(() => {
        const el = this.$refs.productSection;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    refreshHomeData() {
      this.loadGuideList();
      this.loadAccountCircles();
      this.fetchProducts();
    },
    loadGuideList() {
      this.guideListLoading = true;
      api.guideList(1)
        .then((result) => {
          var list = (result && result.data) || [];
          this.guideListAll = list;
          this.guideTotal = Number(result && result.total) || list.length;
        })
        .catch(() => {
          this.guideListAll = [];
          this.guideTotal = 0;
        })
        .finally(() => {
          this.guideListLoading = false;
        });
    },
    loadAccountCircles() {
      const cached = this.normalizeCircles(store.state.userinfo && store.state.userinfo.circle_list);
      this.accountCircles = cached;

      if (!store.state.token) {
        return Promise.resolve(cached);
      }

      return api.userInfo(store.state.token)
        .then((profile) => {
          store.setUserinfo(profile || {});
          this.accountCircles = this.normalizeCircles(profile && profile.circle_list);
          return this.accountCircles;
        })
        .catch(() => cached);
    },
    normalizeCircles(list) {
      if (!Array.isArray(list)) return [];
      return list.filter(function (circle) {
        return circle && circle.id != null;
      }).map(function (circle) {
        return {
          id: circle.id,
          title: circle.title || '',
          momo: circle.momo || '',
          city: circle.city || '',
          contry: circle.contry || circle.country || ''
        };
      });
    },
    fetchProducts(options) {
      options = options || {};
      const keyword = this.getSearchKeyword();
      const requestId = this.productsRequestId + 1;
      this.productsRequestId = requestId;
      this.productsLoading = true;
      this.productsError = '';
      var token = store.state.token || '';
      var params = {
        limit: 20,
        page: 1,
        search: keyword,
        order: 'createAt',
        sort: 'desc'
      };
      api.goodsList(params, token)
        .then((result) => {
          if (requestId !== this.productsRequestId) return;
          var list = (result && result.data) || [];
          this.activeSearchKeyword = keyword;
          this.products = list.map(function (item) {
            return {
              id: item.id,
              image: item.image || FALLBACK_IMG,
              alt: item.title || '',
              title: item.title || '',
              price: item.price ? '£' + Number(item.price).toFixed(2) : '',
              location: item.place || item.city || '',
              time: ''
            };
          });
          if (options.scrollToResults && keyword) {
            this.scrollToProductSection();
          }
        })
        .catch((err) => {
          if (requestId !== this.productsRequestId) return;
          this.productsError = err.message || '商品加载失败';
        })
        .finally(() => {
          if (requestId === this.productsRequestId) {
            this.productsLoading = false;
          }
        });
    }
  },
  mounted() {
    const container = this.getScrollContainer();
    if (container) {
      container.addEventListener('scroll', this.onScroll, { passive: true });
    }
    this.refreshHomeData();
    this.$nextTick(() => {
      this.homeDataLoaded = true;
    });
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    const container = this.getScrollContainer();
    if (container) {
      container.removeEventListener('scroll', this.onScroll);
    }
  }
};
</script>

<style scoped>
body {
  font-family: 'Inter', sans-serif;
  -webkit-tap-highlight-color: transparent;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.glass-effect {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  -webkit-appearance: none;
  appearance: none;
}
.guide-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 0 16px 4px;
  scroll-snap-type: x proximity;
}
.guide-card {
  width: 156px;
  min-width: 156px;
  min-height: 92px;
  border-radius: 10px;
  padding: 12px;
  display: grid;
  grid-template-columns: 32px 1fr;
  grid-template-rows: auto 1fr;
  column-gap: 10px;
  row-gap: 4px;
  text-align: left;
  scroll-snap-align: start;
}
.guide-card-icon {
  grid-row: 1 / span 2;
  font-size: 26px;
  margin-top: 2px;
}
.guide-card-title,
.guide-card-desc {
  min-width: 0;
}
.guide-card-title {
  font-weight: 600;
  line-height: 1.3;
}
.guide-card-desc {
  line-height: 1.35;
}
.guide-card-avatar {
  grid-row: 1 / span 2;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
  margin-top: 2px;
}
.card-fav-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.masonry-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
</style>
